// @section 1 Atlas Deals Pipeline — Patrick's CPA-acquisition daily ops surface.
//
// Two-pane layout: left = call queue (owner=patrick, urgency-bucketed),
// right = deal inbox (one row per active deal w/ stage + CIM-on-disk + counts).
// Backed by /api/hq/acquisitions/* over sandbox_poc.acquisitions.v_call_queue +
// v_deal_inbox_status. Reads-only — write paths land later.

import { useEffect, useMemo, useState } from 'react';
import { useIsMobile } from '../lib/useIsMobile';
import {
  AcquisitionsCallQueue,
  AcquisitionsCallQueueItem,
  AcquisitionsDealInbox,
  AcquisitionsDealInboxRow,
  getAcquisitionsCallQueue,
  getAcquisitionsDealInbox,
} from '../api';

// @block 1.1 Date helpers
function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso ?? '—';
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  return sameDay
    ? d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function fmtMoney(n: number | null): string {
  if (n == null) return '—';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${Math.round(n).toLocaleString()}`;
}

// @block 1.2 Urgency styling — color the urgency pill consistent with operator intuition
function urgencyPill(u: AcquisitionsCallQueueItem['urgency']): { label: string; bg: string; fg: string } {
  switch (u) {
    case 'overdue': return { label: 'overdue', bg: '#3a1414', fg: '#f5a3a3' };
    case 'today':   return { label: 'today',   bg: '#3a3014', fg: '#f5d68a' };
    case 'soon':    return { label: 'soon',    bg: '#14243a', fg: '#8acdf5' };
    case 'later':   return { label: 'later',   bg: '#1f2429', fg: '#8a99a8' };
    case 'undated': return { label: 'undated', bg: '#1f2429', fg: '#6a7480' };
    default:        return { label: '—',       bg: '#1f2429', fg: '#6a7480' };
  }
}

// @block 1.3 Stage styling — color stages by buyer-side progression
function stagePill(stage: string | null): { label: string; bg: string; fg: string } {
  const s = stage ?? '—';
  if (s === 'closed_won')                                                       return { label: s, bg: '#143a1c', fg: '#9ce0ad' };
  if (s === 'closed_lost')                                                      return { label: s, bg: '#2c1a1a', fg: '#c69a9a' };
  if (s === 'under_loi' || s === 'loi_sent' || s === 'ioi_sent')                return { label: s, bg: '#1a2c3a', fg: '#9ccfe0' };
  if (s === 'cim_received' || s === 'meeting')                                  return { label: s, bg: '#1f3a2c', fg: '#9ce0c3' };
  if (s === 'nda_signed' || s === 'nda_pending')                                return { label: s, bg: '#2a2a3a', fg: '#c4c4e6' };
  if (s === 'inquiry_sent')                                                     return { label: s, bg: '#1f2429', fg: '#8a99a8' };
  return { label: s, bg: '#1f2429', fg: '#8a99a8' };
}

// @section 2 Component
export default function DealsPipeline() {
  // @block 1.4 Phone: own the scroll + stack the 1fr/1fr two-pane to one column.
  const isMobile = useIsMobile();
  const [queue, setQueue] = useState<AcquisitionsCallQueue | null>(null);
  const [inbox, setInbox] = useState<AcquisitionsDealInbox | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [stageFilter, setStageFilter] = useState<string | null>(null);
  const [urgencyFilter, setUrgencyFilter] = useState<string | null>(null);

  // @block 2.1 Bootstrap — fetch both panes in parallel
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [q, i] = await Promise.all([
          getAcquisitionsCallQueue('patrick', urgencyFilter ?? undefined),
          getAcquisitionsDealInbox(stageFilter ?? undefined),
        ]);
        if (!alive) return;
        setQueue(q);
        setInbox(i);
      } catch (e) {
        if (alive) setError(String(e));
      }
    })();
    return () => { alive = false; };
  }, [stageFilter, urgencyFilter]);

  // @block 2.2 Stage counts for the right-pane chips
  const stageCounts = useMemo(() => {
    const m = new Map<string, number>();
    if (!inbox) return m;
    for (const d of inbox.deals) {
      const s = d.stage ?? '—';
      m.set(s, (m.get(s) ?? 0) + 1);
    }
    return m;
  }, [inbox]);

  // @block 2.3 Urgency counts for the left-pane chips
  const urgencyCounts = useMemo(() => {
    const m = new Map<string, number>();
    if (!queue) return m;
    for (const it of queue.items) {
      const u = it.urgency ?? 'undated';
      m.set(u, (m.get(u) ?? 0) + 1);
    }
    return m;
  }, [queue]);

  return (
    <div style={{
      padding: isMobile ? '14px 14px' : '20px 24px', color: 'var(--v5-text, #e6e8eb)',
      ...(isMobile ? { height: '100%', overflowY: 'auto', WebkitOverflowScrolling: 'touch' } : null),
    }}>
      <header style={{ display: 'flex', alignItems: 'baseline', gap: 16, marginBottom: 12, flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>Deals Pipeline</h1>
        <span style={{ fontSize: 12, opacity: 0.6 }}>
          Vertis Holdings CPA-acquisition pipeline · sandbox_poc.acquisitions
        </span>
        <span style={{ marginLeft: 'auto', fontSize: 12, opacity: 0.7 }}>
          {queue?.count ?? '—'} open work · {inbox?.count ?? '—'} active deals
        </span>
      </header>

      {error && (
        <div style={{ marginBottom: 12, padding: '8px 12px', background: '#3a1414',
                      border: '1px solid #6e2222', borderRadius: 6, fontSize: 13 }}>
          {error}
          <button onClick={() => setError(null)} style={{ marginLeft: 12, background: 'transparent',
                  color: '#fff', border: '1px solid #6e2222', borderRadius: 4, padding: '2px 8px',
                  cursor: 'pointer' }}>dismiss</button>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 16 : 24, alignItems: 'start' }}>
        {/* @block 2.4 Left pane — Call queue (Patrick's open work) */}
        <section>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 8 }}>
            <h2 style={{ fontSize: 14, fontWeight: 600, margin: 0, textTransform: 'uppercase',
                         letterSpacing: 0.6, opacity: 0.85 }}>Call Queue · Patrick</h2>
            <div style={{ display: 'flex', gap: 6 }}>
              {(['overdue','today','soon','later','undated'] as const).map(u => {
                const c = urgencyCounts.get(u) ?? 0;
                const active = urgencyFilter === u;
                return (
                  <button key={u}
                    onClick={() => setUrgencyFilter(active ? null : u)}
                    style={{ background: active ? '#2a3a4a' : 'transparent',
                             border: '1px solid #2a3038',
                             color: 'inherit', borderRadius: 12,
                             padding: '2px 8px', fontSize: 11, cursor: 'pointer' }}>
                    {u} · {c}
                  </button>
                );
              })}
            </div>
          </div>
          <div style={{ border: '1px solid #2a3038', borderRadius: 6, overflow: 'hidden' }}>
            {queue?.items.length === 0 && (
              <div style={{ padding: 16, fontSize: 13, opacity: 0.6 }}>
                {urgencyFilter
                  ? `No ${urgencyFilter} items for Patrick.`
                  : 'Queue is empty — nothing owed today.'}
              </div>
            )}
            {queue?.items.map((it, idx) => {
              const u = urgencyPill(it.urgency);
              return (
                <div key={it.activity_id ?? idx}
                  style={{ borderTop: idx === 0 ? 'none' : '1px solid #1f2429',
                           padding: '10px 12px', display: 'grid',
                           gridTemplateColumns: '90px 1fr auto', gap: 12, alignItems: 'start' }}>
                  <div>
                    <span style={{ display: 'inline-block', padding: '2px 8px',
                                   borderRadius: 10, fontSize: 10, fontWeight: 600,
                                   background: u.bg, color: u.fg }}>
                      {u.label}
                    </span>
                    <div style={{ fontSize: 10, opacity: 0.55, marginTop: 4 }}>
                      due {fmtDate(it.next_action_due)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>
                      {it.counterparty_name ?? it.broker_company ?? 'Unknown'}
                      {it.broker_company && it.counterparty_name && (
                        <span style={{ opacity: 0.55, fontWeight: 400 }}> · {it.broker_company}</span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>
                      {it.source_listing_id ? `[${it.source_listing_id}] ` : ''}
                      {it.listing_name ?? '(broker-level)'}
                      {it.listing_state && <span style={{ opacity: 0.55 }}> · {it.listing_state}</span>}
                    </div>
                    <div style={{ fontSize: 12, marginTop: 6, opacity: 0.85 }}>
                      {it.next_action ?? '—'}
                    </div>
                  </div>
                  <div style={{ fontSize: 11, opacity: 0.55, whiteSpace: 'nowrap' }}>
                    {it.deal_stage && (
                      <div style={{ ...stagePill(it.deal_stage),
                                    display: 'inline-block', padding: '2px 6px',
                                    borderRadius: 4, fontSize: 10, fontWeight: 500,
                                    background: stagePill(it.deal_stage).bg,
                                    color: stagePill(it.deal_stage).fg }}>
                        {stagePill(it.deal_stage).label}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* @block 2.5 Right pane — Deal inbox (one row per active deal) */}
        <section>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 8 }}>
            <h2 style={{ fontSize: 14, fontWeight: 600, margin: 0, textTransform: 'uppercase',
                         letterSpacing: 0.6, opacity: 0.85 }}>Deal Inbox</h2>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {Array.from(stageCounts.entries()).slice(0, 6).map(([s, c]) => {
                const active = stageFilter === s;
                return (
                  <button key={s}
                    onClick={() => setStageFilter(active ? null : s)}
                    style={{ background: active ? '#2a3a4a' : 'transparent',
                             border: '1px solid #2a3038',
                             color: 'inherit', borderRadius: 12,
                             padding: '2px 8px', fontSize: 11, cursor: 'pointer' }}>
                    {s} · {c}
                  </button>
                );
              })}
            </div>
          </div>
          <div style={{ border: '1px solid #2a3038', borderRadius: 6, overflow: 'hidden' }}>
            {inbox?.deals.length === 0 && (
              <div style={{ padding: 16, fontSize: 13, opacity: 0.6 }}>
                No deals match the filter.
              </div>
            )}
            {inbox?.deals.map((d: AcquisitionsDealInboxRow, idx: number) => {
              const sp = stagePill(d.stage);
              return (
                <div key={d.deal_id ?? idx}
                  style={{ borderTop: idx === 0 ? 'none' : '1px solid #1f2429',
                           padding: '10px 12px', display: 'grid',
                           gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'start' }}>
                  <div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span style={{ ...sp, display: 'inline-block', padding: '2px 8px',
                                     borderRadius: 4, fontSize: 10, fontWeight: 500,
                                     background: sp.bg, color: sp.fg }}>
                        {sp.label}
                      </span>
                      {d.cim_on_disk && (
                        <span style={{ fontSize: 10, opacity: 0.75 }} title="CIM file is on disk">
                          📄 CIM
                        </span>
                      )}
                      {d.priority && d.priority !== 'normal' && (
                        <span style={{ fontSize: 10, opacity: 0.75 }}>
                          {d.priority}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600, marginTop: 4 }}>
                      {d.business_name ?? d.source_listing_id ?? 'Unnamed deal'}
                      {d.location_state && (
                        <span style={{ opacity: 0.55, fontWeight: 400 }}> · {d.location_state}</span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>
                      {d.broker_company ?? '—'}
                      {d.source_listing_id && (
                        <span style={{ opacity: 0.55 }}> · {d.source_listing_id}</span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, marginTop: 6, display: 'flex', gap: 12, opacity: 0.8 }}>
                      <span title="asking price">ask {fmtMoney(d.asking_price)}</span>
                      <span title="annual revenue">rev {fmtMoney(d.revenue)}</span>
                      {d.cim_count > 0 && <span>· {d.cim_count} CIMs</span>}
                      {d.nda_doc_count > 0 && <span>· {d.nda_doc_count} NDAs</span>}
                      {d.call_count > 0 && <span>· {d.call_count} calls</span>}
                      {d.email_count > 0 && <span>· {d.email_count} emails</span>}
                    </div>
                    {d.next_action && (
                      <div style={{ fontSize: 12, marginTop: 6, opacity: 0.85 }}>
                        → {d.next_action}
                        {d.next_action_owner && (
                          <span style={{ opacity: 0.55 }}> ({d.next_action_owner})</span>
                        )}
                      </div>
                    )}
                    {d.last_summary && (
                      <div style={{ fontSize: 11, marginTop: 4, opacity: 0.6, fontStyle: 'italic' }}>
                        last: {d.last_summary}
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: 10, opacity: 0.55, whiteSpace: 'nowrap', textAlign: 'right' }}>
                    {fmtDate(d.last_touched_at)}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}
